import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

import { UserPlus, Lock, Unlock, Edit, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AddEditUser } from "./AddEditUser";
import API from "../api/api01";

interface User {
  id: string | number;
  name: string;
  email: string;
  role: string;
  status: "Active" | "Blocked";
  department?: string;
  course?: string;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState({
    view: true,
    insert: true,
    update: true,
    delete: true
  });

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await API.get("/superadmin/users");
      setUsers(data);
    } catch (error) {
      toast.error("Failed to load users from server.");
    }
  };

  const fetchPermissions = async () => {
    try {
      // Slug for User Management is 'users'
      const { data } = await API.get("/superadmin/module-permissions/users");
      setPermissions(data);
    } catch (error) {
      console.error("Failed to fetch permissions", error);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const toggleUserStatus = async (id: string | number, currentStatus: string) => {
    if (!permissions.delete && !permissions.update) {
      toast.error("You do not have permission to modify user status.");
      return;
    }
    try {
      const newStatus = currentStatus === "Active" ? "Blocked" : "Active";
      await API.put(`/superadmin/users/${id}/status`, { status: newStatus });
      toast.success(`User access ${newStatus.toLowerCase()} successfully!`);
      // Optimistically update the local state while waiting for the next fetch
      setUsers(users.map(u => u.id === id ? { ...u, status: newStatus as "Active" | "Blocked" } : u));
      fetchUsers();
    } catch (err) {
      toast.error("Failed to update user status.");
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const students = filteredUsers.filter(u => u.role === "Student" || u.role === "student");
  const teachers = filteredUsers.filter(u => u.role === "Teacher" || u.role === "teacher");
  const admins = filteredUsers.filter(u => u.role === "Admin" || u.role === "admin" || u.role === "Superadmin" || u.role === "superadmin");

  const handleDeleteUser = async (id: string | number) => {
    if (!permissions.delete) {
      toast.error("You do not have permission to delete users.");
      return;
    }
    if (!window.confirm("Are you sure you want to permanently delete this user? This action cannot be undone.")) {
      return;
    }

    try {
      await API.delete(`/superadmin/users/${id}`);
      toast.success("User deleted permanently.");
      fetchUsers();
    } catch (err) {
      toast.error("Failed to delete user.");
    }
  };

  const renderActions = (user: User) => (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => setEditingUser(user)}
        disabled={!permissions.update}
      >
        <Edit className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => toggleUserStatus(user.id, user.status)}
        className="h-8 w-8 p-0"
        disabled={!permissions.delete && !permissions.update}
      >
        {user.status === "Active" ? (
          <Lock className="h-4 w-4 text-destructive" />
        ) : (
          <Unlock className="h-4 w-4 text-primary" />
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleDeleteUser(user.id)}
        className="h-8 w-8 p-0"
        disabled={!permissions.delete}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );

  if (isCreatingUser) {
    return (
      <AddEditUser
        mode="create"
        currentUserRole="Admin"
        onNavigate={() => {
          setIsCreatingUser(false);
          fetchUsers();
        }}
      />
    );
  }

  if (editingUser) {
    return (
      <AddEditUser
        user={editingUser as any}
        currentUserRole="Admin"
        onNavigate={(page) => {
          if (page === 'view-profile') {
            setEditingUser(null);
            fetchUsers();
          }
        }}
      />
    );
  }

  return (
    <Card className="bg-card border-white/10 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">User Management</CardTitle>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64 bg-background border-white/5"
              />
            </div>
            <Button
              className="gap-2 text-white bg-blue-600 hover:bg-blue-700"
              onClick={() => setIsCreatingUser(true)}
            >
              <UserPlus className="h-4 w-4" />
              Add User
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="students" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md mb-6 bg-muted/20 border border-white/5">
            <TabsTrigger value="students">Students</TabsTrigger>
            <TabsTrigger value="teachers">Teachers</TabsTrigger>
            <TabsTrigger value="admins">Admins</TabsTrigger>
          </TabsList>

          <TabsContent value="students" className="mt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Department</TableHead>
                    <TableHead className="hidden lg:table-cell">Course</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((user) => (
                    <TableRow key={user.id} className="border-white/5 hover:bg-white/5 transition-colors">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{user.email}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{user.department}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{user.course}</TableCell>
                      <TableCell>
                        <Badge className={user.status === "Active" ? "bg-primary/10 text-primary hover:bg-primary/20" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{renderActions(user)}</TableCell>
                    </TableRow>
                  ))}
                  {students.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">No students found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="teachers" className="mt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teachers.map((user) => (
                    <TableRow key={user.id} className="border-white/5 hover:bg-white/5 transition-colors">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{user.email}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{user.department}</TableCell>
                      <TableCell>
                        <Badge className={user.status === "Active" ? "bg-primary/10 text-primary hover:bg-primary/20" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{renderActions(user)}</TableCell>
                    </TableRow>
                  ))}
                  {teachers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">No teachers found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="admins" className="mt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{user.email}</TableCell>
                      <TableCell>
                        <Badge className={user.status === "Active" ? "bg-primary/10 text-primary hover:bg-primary/20" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{renderActions(user)}</TableCell>
                    </TableRow>
                  ))}
                  {admins.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No admins found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
